// @ts-nocheck
import { createContext, useContext, useEffect, useState } from "react";

export type Lang = "en" | "he";

/* ─────────────────────────────────────────────────────────────────────────────
   TRANSLATIONS
───────────────────────────────────────────────────────────────────────────── */
const en = {
  brand: "WaSender",
  brandSub: "WhatsApp Bulk Sender",
  settings: "Settings",
  logout: "Logout",
  version: "WaSender v1.0",
  common: {
    cancel: "Cancel",
    save: "Save Changes",
    saving: "Saving…",
    create: "Create Now",
    creating: "Creating…",
    delete: "Delete",
    edit: "Edit",
    search: "Search…",
    noData: "No data found",
    loading: "Loading…",
    active: "Active",
    inactive: "Inactive",
    connected: "Connected",
    connecting: "Connecting",
    pending: "Pending",
    sent: "Sent",
    delivered: "Delivered",
    failed: "Failed",
    read: "Read",
    all: "All",
    name: "Name",
    phone: "Phone Number",
    optional: "optional",
    type: "Type",
    status: "Status",
    date: "Date",
    actions: "Actions",
    yes: "Yes",
    no: "No",
    confirm: "Confirm",
    success: "Success",
  },
  nav: {
    "/dashboard": "Dashboard",
    "/devices": "My Devices",
    "/templates": "Templates",
    "/single-send": "Single Send",
    "/chatbot": "Chatbot (Auto Reply)",
    "/ai-bot": "AI Bot",
    "/my-apps": "My Apps",
    "/contacts": "Contact Book",
    "/campaigns": "Send Bulk Message",
    "/scheduled": "Scheduled Message",
    "/messages": "Message Log",
    "/webhooks": "Webhook Logs",
    "/subscription": "Subscription",
    "/support": "Help & Support",
    "/profile": "Profile Settings",
    "/auth-key": "Auth Key",
  },
  dashboard: {
    title: "Dashboard",
    createDevice: "Create Device",
    sendMessage: "Send a Message",
    totalDevices: "Total Devices",
    totalMessages: "Total Messages",
    pendingSchedules: "Pending Schedules",
    totalContacts: "Total Contacts",
    messagesTransaction: "Messages Transaction",
    automaticReplies: "Automatic Replies",
    messages: "Messages",
    devicesStatistics: "Devices Statistics",
    last7Days: "Last 7 Days",
    today: "Today",
    last30Days: "Last 30 Days",
    sent: "Sent",
    delivered: "Delivered",
    failed: "Failed",
    read: "Read",
    autoReplies: "Auto Replies",
    noDevices: "No devices connected yet",
    addDevice: "Add a device",
    days: { Mon: "Mon", Tue: "Tue", Wed: "Wed", Thu: "Thu", Fri: "Fri", Sat: "Sat", Sun: "Sun" },
  },
  devices: {
    title: "My Devices",
    createDevice: "Create Device",
    totalDevices: "Total Devices",
    activeDevices: "Active Devices",
    inactiveDevices: "Inactive Devices",
    createNew: "Create New Device",
    deviceName: "Device Name",
    phoneNumber: "Phone Number",
    webhookUrl: "Webhook URL",
    webhookHint: "WaSender will send events via POST to this URL",
    deviceNamePlaceholder: "My iPhone 13 Pro",
    phonePlaceholder: "Enter phone number with country code",
    noDevices: "No Devices Found",
    noDevicesHint: "Connect your first WhatsApp account to get started",
    lastSeen: "Last seen:",
    neverConnected: "Never connected — tap Scan QR to link",
    relinkQr: "Re-link via QR",
    scanQr: "Scan QR Code",
    connected: "Connected",
    connecting: "Connecting",
    pending: "Pending",
    deleteConfirm: "Delete this device?",
  },
  templates: {
    title: "Templates",
    newTemplate: "New Template",
    totalTemplates: "Total Templates",
    totalUsed: "Times Used",
    categories: "Categories",
    templateName: "Template Name",
    content: "Message Content",
    category: "Category",
    mediaType: "Media Type",
    text: "Text",
    image: "Image",
    video: "Video",
    uploadImage: "Click to upload image",
    uploadVideo: "Click to upload video",
    noTemplates: "No Templates Yet",
    noTemplatesHint: "Create your first message template to get started",
    cats: { All: "All", General: "General", Promotion: "Promotion", Notification: "Notification", Reminder: "Reminder", Alert: "Alert" },
    namePlaceholder: "e.g. Welcome Message",
    contentPlaceholder: "Hello {name}, welcome to our service!",
    preview: "Preview",
    deleteConfirm: "Delete this template?",
    textOnly: "Text Only",
    textImage: "Text + Image",
    textVideo: "Text + Video",
    uploading: "Uploading {pct}%",
    clickToChange: "Click to change",
    uploaded: "Uploaded",
    imageUploadHint: "JPG, PNG, GIF up to 16MB",
    videoUploadHint: "MP4, MOV, AVI up to 64MB",
    personalizationHint: "Use {{name}}, {{company}} for personalization",
    createTemplate: "Create Template",
    copyText: "Copy Text",
    noTemplatesFound: "No templates found",
    uploadFailed: "Upload failed. Please try again.",
    uploadRequired: "Please upload a {type} before saving.",
    createFailed: "Failed to create template. Please try again.",
    buttons: "Quick Reply Buttons",
    buttonsHint: "Add up to 3 buttons (max 20 characters each). Recipients will see them as tappable buttons.",
    addButton: "Add Button",
    buttonPlaceholder: "e.g. Yes, No, Learn More",
    noButtons: "No buttons",
    duplicate: "Duplicate",
  },
  campaigns: {
    title: "Send Bulk Message",
    newCampaign: "New Campaign",
    totalCampaigns: "Total Campaigns",
    completed: "Completed",
    running: "Running",
    campaignName: "Campaign Name",
    device: "Device",
    template: "Template",
    group: "Contact Group",
    message: "Message",
    delayMin: "Min Delay (seconds)",
    delayMax: "Max Delay (seconds)",
    noCampaigns: "No Campaigns Yet",
    noCampaignsHint: "Create your first bulk message campaign",
    start: "Start",
    pause: "Pause",
    resume: "Resume",
    resend: "Resend",
    detail: "Detail",
    status: { draft: "Draft", running: "Running", paused: "Paused", completed: "Completed", failed: "Failed" },
    selectDevice: "Select device…",
    selectTemplate: "Select template (optional)…",
    selectGroup: "Select group (optional)…",
    orTypeMessage: "Or type a message directly:",
    maxDelayError: "Max delay must be greater than min delay.",
    deviceNotConnected: "Device is not connected to WhatsApp.\nPlease go to My Devices and scan the QR code first.",
    progress: "Progress",
    recipients: "recipients",
    remaining: "remaining",
    delayTitle: "Random Delay Between Messages",
    minDelay: "Min delay",
    maxDelay: "Max delay",
    delayHint: "Each message will wait a random time between {min} and {max} before the next one.",
    duplicate: "Duplicate",
    search: "Search campaigns…",
    filterStatus: "All Statuses",
  },
  singleSend: {
    title: "Single Send",
    sendMessage: "Send Message",
    sendCustom: "Send Custom Message",
    plain: "Plain Text",
    media: "Media",
    location: "Location",
    device: "WhatsApp Device",
    phone: "Recipient Phone Number",
    message: "Message",
    template: "Use Template",
    latitude: "Latitude",
    longitude: "Longitude",
    sending: "Sending…",
    sent: "Message sent successfully!",
    selectDevice: "Select device…",
    selectTemplate: "No template",
    phonePlaceholder: "+1234567890",
    messagePlaceholder: "Type your message here…",
    deviceNotConnected: "Device is not connected to WhatsApp. Please scan the QR code first.",
    attachMedia: "Attach Media",
    uploadHint: "Click to upload or drag & drop",
    uploadTypes: "Images, videos, documents up to 16MB",
  },
  contacts: {
    title: "Contact Book",
    addContact: "Add Contact",
    importContacts: "Import Contacts",
    newGroup: "New Group",
    totalContacts: "Total Contacts",
    totalGroups: "Groups",
    contactName: "Name",
    contactPhone: "Phone Number",
    contactGroup: "Group",
    groupName: "Group Name",
    allContacts: "All Contacts",
    search: "Search contacts…",
    noContacts: "No Contacts Found",
    noContactsHint: "Add or import contacts to get started",
    deleteSelected: "Delete Selected",
    deleteConfirm: "Delete selected contacts?",
    groupDeleteConfirm: "Delete this group and all its contacts?",
    importing: "Importing…",
    importSuccess: "Imported {n} contacts, skipped {s}",
    importError: "Import failed",
    dragDrop: "Drag & drop a file here, or click to browse",
    selectGroup: "Select group (optional)…",
    namePlaceholder: "John Doe",
    phonePlaceholder: "+1234567890",
    assignToGroup: "Assign to Group",
    noGroup: "No group",
    autoDetect: "The system auto-detects phone and name columns regardless of their header name. Make sure your file has at least one phone number column.",
    importedSuccess: "{n} contacts imported successfully!",
    skippedRows: "{n} rows skipped (missing or invalid phone number)",
    close: "Close",
    groups: "Groups",
    createGroup: "Create Group",
    groupOptional: "Group (optional)",
    deselectAll: "Deselect all",
    deleting: "Deleting…",
    fullName: "Full Name",
    csvAutoDetect: "CSV, Excel, XML, JSON, TSV — auto-detected",
    fileTypes: "CSV · Excel (xlsx/xls) · XML · JSON · TSV · ODS",
    contactsSelected: "contacts selected",
    moveToGroup: "Move to Group",
    moveBulk: "Move to Group",
    moving: "Moving…",
    removeFromGroup: "Remove from Group",
    importFromWa: "Import from WhatsApp",
    waImportTitle: "Import WhatsApp Contacts",
    waImportDesc: "Select a connected device to load all phone numbers saved in WhatsApp",
    waSelectDevice: "Select a device…",
    waLoadContacts: "Load Contacts",
    waLoading: "Loading contacts from WhatsApp…",
    waSourceContacts: "Saved Contacts",
    waSourceChats: "Conversations",
    waSourceAll: "All (Merged)",
    waSourceContactsDesc: "Phone numbers saved in your WhatsApp contacts list",
    waSourceChatsDesc: "Phone numbers from actual WhatsApp conversations (including unsaved numbers)",
    waSourceAllDesc: "All phone numbers: saved contacts + conversations merged and deduplicated",
    waNoContacts: "No contacts found for this device.",
    waNoContactsHint: "WhatsApp only syncs contacts on first connection. Please disconnect and reconnect your device once to sync contacts — after that they'll be stored permanently.",
    waGoToDevices: "Go to My Devices",
    waSelectAll: "Select all",
    waDeselectAll: "Deselect all",
    waImportGroup: "Save to group",
    waNewGroup: "New group name…",
    waImporting: "Importing…",
    waImportBtn: "Import Selected",
    waImportDone: "Imported {n} contacts, {s} skipped (duplicates)",
    waContactsLoaded: "{n} contacts found",
    waGroupCreate: "Create new group",
    waExistingGroup: "Use existing group",
  },
  messages: {
    title: "Message Log",
    totalMessages: "Total Messages",
    delivered: "Delivered",
    pending: "Pending",
    failed: "Failed",
    tabs: { all: "All", pending: "Pending", sent: "Sent", delivered: "Delivered", failed: "Failed", read: "Read" },
    recipient: "Recipient",
    device: "Device",
    content: "Content",
    noMessages: "No Messages Found",
  },
  scheduled: {
    title: "Scheduled Message",
    newSchedule: "New Schedule",
    totalSchedules: "Total Schedules",
    pendingSchedules: "Pending",
    sentSchedules: "Sent",
    scheduleName: "Name",
    device: "Device",
    template: "Template",
    group: "Contact Group",
    scheduledAt: "Scheduled Date & Time",
    noSchedules: "No Scheduled Messages",
    noSchedulesHint: "Create a scheduled message to send later",
    status: { pending: "Pending", sent: "Sent", failed: "Failed" },
    selectDevice: "Select device…",
    selectTemplate: "Select template (optional)…",
    selectGroup: "Select group (optional)…",
    deleteConfirm: "Delete this scheduled message?",
    editSchedule: "Edit Schedule",
    saveChanges: "Save Changes",
    messageText: "Message Text",
    messageHint: "Type message here, or leave empty to use the selected template",
    templateOrMessage: "Use a template OR type a custom message below",
    recurrence: "Recurrence",
    recurrenceOptions: { none: "No recurrence", daily: "Daily", weekly: "Weekly", monthly: "Monthly" },
  },
  chatbot: {
    title: "Chatbot (Auto Reply)",
    newRule: "New Rule",
    totalRules: "Total Rules",
    activeRules: "Active Rules",
    triggered: "Triggered Today",
    keyword: "Keyword",
    response: "Response",
    type: "Type",
    text: "Text",
    template: "Template",
    noRules: "No Auto-Reply Rules",
    noRulesHint: "Add keywords that trigger automatic replies",
    keywordPlaceholder: "e.g. hello, price, hours",
    responsePlaceholder: "The bot will reply with this message",
    deleteConfirm: "Delete this rule?",
  },
  webhooks: {
    title: "Webhook Logs",
    configure: "Configure Webhook",
    successCount: "Success",
    failedCount: "Failed",
    pendingCount: "Pending",
    event: "Event",
    url: "URL",
    code: "Code",
    retry: "Retry",
    noLogs: "No Webhook Logs",
    filter: { all: "All", success: "Success", failed: "Failed", pending: "Pending" },
    setupTitle: "Configure Webhook URL",
    setupHint: "WaSender will send POST events to this URL when messages are sent or received.",
    webhookUrlLabel: "Webhook URL",
    save: "Save",
  },
  profile: {
    title: "Profile Settings",
    personalInfo: "Personal Information",
    fullName: "Full Name",
    email: "Email Address",
    phone: "Phone Number",
    changePhoto: "Change photo",
    saveChanges: "Save Changes",
    saving: "Saving…",
    saved: "Profile updated successfully!",
    changePassword: "Change Password",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmPassword: "Confirm New Password",
    updatePassword: "Update Password",
    notifications: "Notification Preferences",
    emailNotifications: "Email Notifications",
    smsNotifications: "SMS Notifications",
  },
  subscription: {
    title: "Subscription",
    currentPlan: "Current Plan",
    businessPlan: "Business Plan",
    renewsOn: "Your subscription renews on May 16, 2026",
    devicesUsed: "Devices Used",
    contactsUsed: "Contacts Used",
    messagesMonth: "Messages This Month",
    upgradeTitle: "Upgrade or Change Plan",
    popular: "Popular",
    currentPlanBtn: "Current Plan",
    switchPlan: "Switch Plan",
    active: "Active",
  },
  support: {
    title: "Help & Support",
    newTicket: "New Ticket",
    faqTitle: "Frequently Asked Questions",
    faqs: [
      "How do I connect my WhatsApp account?",
      "How does bulk messaging work?",
      "What is the message delivery rate limit?",
      "How to set up auto-replies (Chatbot)?",
      "How do I import contacts?",
    ],
    submitTicket: "Submit Support Ticket",
    subject: "Subject",
    subjectPlaceholder: "Describe your issue briefly",
    message: "Message",
    messagePlaceholder: "Provide more details…",
    submit: "Submit Ticket",
    myTickets: "My Tickets",
    open: "Open",
    resolved: "Resolved",
    noTickets: "No support tickets yet",
    noTicketsHint: "Create a ticket if you need help",
  },
  myApps: {
    title: "My Apps",
    newApp: "New App",
    totalApps: "Total Apps",
    activeApps: "Active",
    totalRequests: "Total Requests",
    appName: "Application Name",
    appType: "Integration Type",
    apiKey: "API Key",
    noApps: "No Apps Yet",
    noAppsHint: "Create an API integration to connect external services",
    appNamePlaceholder: "My CRM Integration",
    deleteConfirm: "Delete this app?",
    active: "Active",
    inactive: "Inactive",
    requests: "requests",
    copyKey: "Copy key",
    copied: "Copied!",
  },
  authKey: {
    title: "Auth Key",
    yourKey: "Your API Key",
    active: "Active",
    keyHint: "Use this key to authenticate API requests from your application.",
    regenerate: "Regenerate Key",
    warning: "Warning: Regenerating your key will invalidate the existing one. All apps using the current key will stop working.",
    copied: "Copied to clipboard!",
    usageTitle: "API Usage",
    usageEndpoint: "Base URL",
    usageAuth: "Authentication",
    usageAuthHint: "Include the API key in the Authorization header:",
  },
};

const he: typeof en = {
  brand: "WaSender",
  brandSub: "שליחת הודעות וואטסאפ",
  settings: "הגדרות",
  logout: "התנתקות",
  version: "WaSender v1.0",
  common: {
    cancel: "ביטול",
    save: "שמור שינויים",
    saving: "שומר…",
    create: "צור עכשיו",
    creating: "יוצר…",
    delete: "מחק",
    edit: "ערוך",
    search: "חיפוש…",
    noData: "לא נמצאו נתונים",
    loading: "טוען…",
    active: "פעיל",
    inactive: "לא פעיל",
    connected: "מחובר",
    connecting: "מתחבר",
    pending: "ממתין",
    sent: "נשלח",
    delivered: "נמסר",
    failed: "נכשל",
    read: "נקרא",
    all: "הכל",
    name: "שם",
    phone: "מספר טלפון",
    optional: "אופציונלי",
    type: "סוג",
    status: "סטטוס",
    date: "תאריך",
    actions: "פעולות",
    yes: "כן",
    no: "לא",
    confirm: "אישור",
    success: "הצלחה",
  },
  nav: {
    "/dashboard": "לוח בקרה",
    "/devices": "המכשירים שלי",
    "/templates": "תבניות",
    "/single-send": "שליחה בודדת",
    "/chatbot": "צ'אטבוט (מענה אוטומטי)",
    "/ai-bot": "בוט AI",
    "/my-apps": "האפליקציות שלי",
    "/contacts": "ספר כתובות",
    "/campaigns": "שליחת הודעה קבוצתית",
    "/scheduled": "הודעה מתוזמנת",
    "/messages": "יומן הודעות",
    "/webhooks": "יומן Webhook",
    "/subscription": "מנוי",
    "/support": "עזרה ותמיכה",
    "/profile": "הגדרות פרופיל",
    "/auth-key": "מפתח אימות",
  },
  dashboard: {
    title: "לוח בקרה",
    createDevice: "הוסף מכשיר",
    sendMessage: "שלח הודעה",
    totalDevices: "סה״כ מכשירים",
    totalMessages: "סה״כ הודעות",
    pendingSchedules: "הודעות ממתינות",
    totalContacts: "סה״כ אנשי קשר",
    messagesTransaction: "פעילות הודעות",
    automaticReplies: "מענה אוטומטי",
    messages: "הודעות",
    devicesStatistics: "סטטיסטיקת מכשירים",
    last7Days: "7 ימים אחרונים",
    today: "היום",
    last30Days: "30 ימים אחרונים",
    sent: "נשלח",
    delivered: "נמסר",
    failed: "נכשל",
    read: "נקרא",
    autoReplies: "מענה אוטו׳",
    noDevices: "אין מכשירים מחוברים עדיין",
    addDevice: "הוסף מכשיר",
    days: { Mon: "שני", Tue: "שלישי", Wed: "רביעי", Thu: "חמישי", Fri: "שישי", Sat: "שבת", Sun: "ראשון" },
  },
  devices: {
    title: "המכשירים שלי",
    createDevice: "הוסף מכשיר",
    totalDevices: "סה״כ מכשירים",
    activeDevices: "מכשירים פעילים",
    inactiveDevices: "מכשירים לא פעילים",
    createNew: "הוסף מכשיר חדש",
    deviceName: "שם המכשיר",
    phoneNumber: "מספר טלפון",
    webhookUrl: "כתובת Webhook",
    webhookHint: "WaSender ישלח אירועים ב-POST לכתובת זו",
    deviceNamePlaceholder: "iPhone 13 Pro שלי",
    phonePlaceholder: "הכנס מספר טלפון עם קידומת מדינה",
    noDevices: "לא נמצאו מכשירים",
    noDevicesHint: "חבר את חשבון הוואטסאפ הראשון שלך כדי להתחיל",
    lastSeen: "נראה לאחרונה:",
    neverConnected: "לא חובר מעולם — סרוק QR כדי לקשר",
    relinkQr: "קישור מחדש ב-QR",
    scanQr: "סרוק קוד QR",
    connected: "מחובר",
    connecting: "מתחבר",
    pending: "ממתין",
    deleteConfirm: "למחוק את המכשיר?",
  },
  templates: {
    title: "תבניות",
    newTemplate: "תבנית חדשה",
    totalTemplates: "סה״כ תבניות",
    totalUsed: "שימושים",
    categories: "קטגוריות",
    templateName: "שם התבנית",
    content: "תוכן ההודעה",
    category: "קטגוריה",
    mediaType: "סוג מדיה",
    text: "טקסט",
    image: "תמונה",
    video: "וידאו",
    uploadImage: "לחץ להעלאת תמונה",
    uploadVideo: "לחץ להעלאת וידאו",
    noTemplates: "אין תבניות עדיין",
    noTemplatesHint: "צור את תבנית ההודעה הראשונה שלך",
    cats: { All: "הכל", General: "כללי", Promotion: "מבצע", Notification: "התראה", Reminder: "תזכורת", Alert: "אזהרה" },
    namePlaceholder: "לדוגמה: הודעת ברוכים הבאים",
    contentPlaceholder: "שלום {name}, ברוכים הבאים לשירות שלנו!",
    preview: "תצוגה מקדימה",
    deleteConfirm: "למחוק את התבנית?",
    textOnly: "טקסט בלבד",
    textImage: "טקסט + תמונה",
    textVideo: "טקסט + וידאו",
    uploading: "מעלה {pct}%",
    clickToChange: "לחץ לשינוי",
    uploaded: "הועלה",
    imageUploadHint: "JPG, PNG, GIF עד 16MB",
    videoUploadHint: "MP4, MOV, AVI עד 64MB",
    personalizationHint: "השתמש ב-{{name}}, {{company}} לפרסונליזציה",
    createTemplate: "צור תבנית",
    copyText: "העתק טקסט",
    noTemplatesFound: "לא נמצאו תבניות",
    uploadFailed: "ההעלאה נכשלה. אנא נסה שוב.",
    uploadRequired: "אנא העלה {type} לפני השמירה.",
    createFailed: "יצירת התבנית נכשלה. אנא נסה שוב.",
    buttons: "כפתורי תשובה מהירה",
    buttonsHint: "הוסף עד 3 כפתורים (עד 20 תווים כל אחד). הנמענים יראו אותם ככפתורים לחיצים.",
    addButton: "הוסף כפתור",
    buttonPlaceholder: "לדוגמה: כן, לא, מידע נוסף",
    noButtons: "ללא כפתורים",
    duplicate: "שכפל",
  },
  campaigns: {
    title: "שליחת הודעה קבוצתית",
    newCampaign: "קמפיין חדש",
    totalCampaigns: "סה״כ קמפיינים",
    completed: "הושלמו",
    running: "פעילים",
    campaignName: "שם הקמפיין",
    device: "מכשיר",
    template: "תבנית",
    group: "קבוצת אנשי קשר",
    message: "הודעה",
    delayMin: "עיכוב מינימלי (שניות)",
    delayMax: "עיכוב מקסימלי (שניות)",
    noCampaigns: "אין קמפיינים עדיין",
    noCampaignsHint: "צור את קמפיין ההודעות הקבוצתיות הראשון",
    start: "התחל",
    pause: "השהה",
    resume: "המשך",
    resend: "שלח שוב",
    detail: "פרטים",
    status: { draft: "טיוטה", running: "פעיל", paused: "מושהה", completed: "הושלם", failed: "נכשל" },
    selectDevice: "בחר מכשיר…",
    selectTemplate: "בחר תבנית (אופציונלי)…",
    selectGroup: "בחר קבוצה (אופציונלי)…",
    orTypeMessage: "או הקלד הודעה ישירות:",
    maxDelayError: "העיכוב המקסימלי חייב להיות גדול מהמינימלי.",
    deviceNotConnected: "המכשיר אינו מחובר לוואטסאפ.\nאנא עבור לדף המכשירים וסרוק את קוד ה-QR.",
    progress: "התקדמות",
    recipients: "נמענים",
    remaining: "נותרו",
    delayTitle: "עיכוב אקראי בין הודעות",
    minDelay: "עיכוב מינימלי",
    maxDelay: "עיכוב מקסימלי",
    delayHint: "כל הודעה תמתין זמן אקראי בין {min} ל-{max} לפני הבאה.",
    duplicate: "שכפל",
    search: "חיפוש קמפיינים…",
    filterStatus: "כל הסטטוסים",
  },
  singleSend: {
    title: "שליחה בודדת",
    sendMessage: "שלח הודעה",
    sendCustom: "שלח הודעה מותאמת",
    plain: "טקסט רגיל",
    media: "מדיה",
    location: "מיקום",
    device: "מכשיר וואטסאפ",
    phone: "מספר טלפון של הנמען",
    message: "הודעה",
    template: "השתמש בתבנית",
    latitude: "קו רוחב",
    longitude: "קו אורך",
    sending: "שולח…",
    sent: "ההודעה נשלחה בהצלחה!",
    selectDevice: "בחר מכשיר…",
    selectTemplate: "ללא תבנית",
    phonePlaceholder: "+972501234567",
    messagePlaceholder: "הקלד את הודעתך כאן…",
    deviceNotConnected: "המכשיר אינו מחובר לוואטסאפ. אנא סרוק את קוד ה-QR תחילה.",
    attachMedia: "צרף מדיה",
    uploadHint: "לחץ להעלאה או גרור ושחרר",
    uploadTypes: "תמונות, וידאו, מסמכים עד 16MB",
  },
  contacts: {
    title: "ספר כתובות",
    addContact: "הוסף איש קשר",
    importContacts: "ייבוא אנשי קשר",
    newGroup: "קבוצה חדשה",
    totalContacts: "סה״כ אנשי קשר",
    totalGroups: "קבוצות",
    contactName: "שם",
    contactPhone: "מספר טלפון",
    contactGroup: "קבוצה",
    groupName: "שם הקבוצה",
    allContacts: "כל אנשי הקשר",
    search: "חיפוש אנשי קשר…",
    noContacts: "לא נמצאו אנשי קשר",
    noContactsHint: "הוסף או ייבא אנשי קשר כדי להתחיל",
    deleteSelected: "מחק נבחרים",
    deleteConfirm: "למחוק את אנשי הקשר שנבחרו?",
    groupDeleteConfirm: "למחוק את הקבוצה וכל אנשי הקשר שלה?",
    importing: "מייבא…",
    importSuccess: "יובאו {n} אנשי קשר, דולגו {s}",
    importError: "הייבוא נכשל",
    dragDrop: "גרור ושחרר קובץ כאן, או לחץ לעיון",
    selectGroup: "בחר קבוצה (אופציונלי)…",
    namePlaceholder: "ישראל ישראלי",
    phonePlaceholder: "+972501234567",
    assignToGroup: "שייך לקבוצה",
    noGroup: "ללא קבוצה",
    autoDetect: "המערכת מזהה אוטומטית עמודות טלפון ושם ללא תלות בשם הכותרת. ודא שיש לפחות עמודת מספר טלפון אחת.",
    importedSuccess: "יובאו {n} אנשי קשר בהצלחה!",
    skippedRows: "דולגו {n} שורות (מספר טלפון חסר או לא תקין)",
    close: "סגור",
    groups: "קבוצות",
    createGroup: "צור קבוצה",
    groupOptional: "קבוצה (אופציונלי)",
    deselectAll: "בטל בחירה",
    deleting: "מוחק…",
    fullName: "שם מלא",
    csvAutoDetect: "CSV, Excel, XML, JSON, TSV — זיהוי אוטומטי",
    fileTypes: "CSV · Excel (xlsx/xls) · XML · JSON · TSV · ODS",
    contactsSelected: "אנשי קשר נבחרו",
    moveToGroup: "העבר לקבוצה",
    moveBulk: "העבר לקבוצה",
    moving: "מעביר…",
    removeFromGroup: "הסר מקבוצה",
    importFromWa: "ייבוא מ-WhatsApp",
    waImportTitle: "ייבוא אנשי קשר מ-WhatsApp",
    waImportDesc: "בחר מכשיר מחובר כדי לטעון את כל הטלפונים השמורים בחשבון ה-WhatsApp שלך",
    waSelectDevice: "בחר מכשיר…",
    waLoadContacts: "טען אנשי קשר",
    waLoading: "טוען אנשי קשר מ-WhatsApp…",
    waSourceContacts: "אנשי קשר שמורים",
    waSourceChats: "שיחות",
    waSourceAll: "הכל (ממוזג)",
    waSourceContactsDesc: "מספרי טלפון השמורים ברשימת אנשי הקשר שלך ב-WhatsApp",
    waSourceChatsDesc: "מספרי טלפון משיחות WhatsApp בפועל (כולל מספרים שאינם שמורים)",
    waSourceAllDesc: "כל המספרים: אנשי קשר שמורים + שיחות, ממוזגים ללא כפילויות",
    waNoContacts: "לא נמצאו אנשי קשר למכשיר זה.",
    waNoContactsHint: "WhatsApp מסנכרן אנשי קשר רק בחיבור ראשוני. נתק וחבר מחדש את המכשיר פעם אחת כדי לסנכרן — לאחר מכן הם ישמרו לצמיתות.",
    waGoToDevices: "עבור למכשירים שלי",
    waSelectAll: "בחר הכל",
    waDeselectAll: "בטל בחירה",
    waImportGroup: "שמור לקבוצה",
    waNewGroup: "שם קבוצה חדשה…",
    waImporting: "מייבא…",
    waImportBtn: "ייבא נבחרים",
    waImportDone: "יובאו {n} אנשי קשר, {s} דולגו (כפולים)",
    waContactsLoaded: "נמצאו {n} אנשי קשר",
    waGroupCreate: "צור קבוצה חדשה",
    waExistingGroup: "השתמש בקבוצה קיימת",
  },
  messages: {
    title: "יומן הודעות",
    totalMessages: "סה״כ הודעות",
    delivered: "נמסרו",
    pending: "ממתינות",
    failed: "נכשלו",
    tabs: { all: "הכל", pending: "ממתין", sent: "נשלח", delivered: "נמסר", failed: "נכשל", read: "נקרא" },
    recipient: "נמען",
    device: "מכשיר",
    content: "תוכן",
    noMessages: "לא נמצאו הודעות",
  },
  scheduled: {
    title: "הודעה מתוזמנת",
    newSchedule: "תזמון חדש",
    totalSchedules: "סה״כ תזמונים",
    pendingSchedules: "ממתינים",
    sentSchedules: "נשלחו",
    scheduleName: "שם",
    device: "מכשיר",
    template: "תבנית",
    group: "קבוצת אנשי קשר",
    scheduledAt: "תאריך ושעה לשליחה",
    noSchedules: "אין הודעות מתוזמנות",
    noSchedulesHint: "צור הודעה מתוזמנת לשליחה בעתיד",
    status: { pending: "ממתין", sent: "נשלח", failed: "נכשל" },
    selectDevice: "בחר מכשיר…",
    selectTemplate: "בחר תבנית (אופציונלי)…",
    selectGroup: "בחר קבוצה (אופציונלי)…",
    deleteConfirm: "למחוק הודעה מתוזמנת זו?",
    editSchedule: "עריכת תזמון",
    saveChanges: "שמור שינויים",
    messageText: "טקסט ההודעה",
    messageHint: "הקלד הודעה כאן, או השאר ריק כדי להשתמש בתבנית שנבחרה",
    templateOrMessage: "השתמש בתבנית או הקלד הודעה מותאמת למטה",
    recurrence: "חזרתיות",
    recurrenceOptions: { none: "ללא חזרה", daily: "יומי", weekly: "שבועי", monthly: "חודשי" },
  },
  chatbot: {
    title: "צ'אטבוט (מענה אוטומטי)",
    newRule: "חוק חדש",
    totalRules: "סה״כ חוקים",
    activeRules: "חוקים פעילים",
    triggered: "הופעלו היום",
    keyword: "מילת מפתח",
    response: "תגובה",
    type: "סוג",
    text: "טקסט",
    template: "תבנית",
    noRules: "אין חוקי מענה אוטומטי",
    noRulesHint: "הוסף מילות מפתח שמפעילות תגובות אוטומטיות",
    keywordPlaceholder: "לדוגמה: שלום, מחיר, שעות",
    responsePlaceholder: "הבוט יגיב עם הודעה זו",
    deleteConfirm: "למחוק חוק זה?",
  },
  webhooks: {
    title: "יומן Webhook",
    configure: "הגדר Webhook",
    successCount: "הצלחה",
    failedCount: "נכשלו",
    pendingCount: "ממתינים",
    event: "אירוע",
    url: "כתובת URL",
    code: "קוד",
    retry: "נסה שוב",
    noLogs: "אין רשומות Webhook",
    filter: { all: "הכל", success: "הצלחה", failed: "נכשלו", pending: "ממתינים" },
    setupTitle: "הגדרת כתובת Webhook",
    setupHint: "WaSender ישלח אירועי POST לכתובת זו כשהודעות נשלחות או מתקבלות.",
    webhookUrlLabel: "כתובת Webhook",
    save: "שמור",
  },
  profile: {
    title: "הגדרות פרופיל",
    personalInfo: "מידע אישי",
    fullName: "שם מלא",
    email: "כתובת אימייל",
    phone: "מספר טלפון",
    changePhoto: "שנה תמונה",
    saveChanges: "שמור שינויים",
    saving: "שומר…",
    saved: "הפרופיל עודכן בהצלחה!",
    changePassword: "שינוי סיסמה",
    currentPassword: "סיסמה נוכחית",
    newPassword: "סיסמה חדשה",
    confirmPassword: "אישור סיסמה חדשה",
    updatePassword: "עדכן סיסמה",
    notifications: "העדפות התראות",
    emailNotifications: "התראות אימייל",
    smsNotifications: "התראות SMS",
  },
  subscription: {
    title: "מנוי",
    currentPlan: "תוכנית נוכחית",
    businessPlan: "תוכנית עסקית",
    renewsOn: "המנוי שלך מתחדש ב-16 מאי 2026",
    devicesUsed: "מכשירים בשימוש",
    contactsUsed: "אנשי קשר בשימוש",
    messagesMonth: "הודעות החודש",
    upgradeTitle: "שדרג או שנה תוכנית",
    popular: "פופולרי",
    currentPlanBtn: "התוכנית הנוכחית",
    switchPlan: "עבור לתוכנית",
    active: "פעיל",
  },
  support: {
    title: "עזרה ותמיכה",
    newTicket: "פנייה חדשה",
    faqTitle: "שאלות נפוצות",
    faqs: [
      "כיצד אני מחבר את חשבון הוואטסאפ שלי?",
      "כיצד שליחת הודעות קבוצתיות עובדת?",
      "מה הגבלת קצב מסירת ההודעות?",
      "כיצד מגדירים מענה אוטומטי (צ'אטבוט)?",
      "כיצד מייבאים אנשי קשר?",
    ],
    submitTicket: "שלח פנייה לתמיכה",
    subject: "נושא",
    subjectPlaceholder: "תאר את הבעיה בקצרה",
    message: "הודעה",
    messagePlaceholder: "ספק פרטים נוספים…",
    submit: "שלח פנייה",
    myTickets: "הפניות שלי",
    open: "פתוח",
    resolved: "נפתר",
    noTickets: "אין פניות תמיכה עדיין",
    noTicketsHint: "צור פנייה אם אתה זקוק לעזרה",
  },
  myApps: {
    title: "האפליקציות שלי",
    newApp: "אפליקציה חדשה",
    totalApps: "סה״כ אפליקציות",
    activeApps: "פעילות",
    totalRequests: "סה״כ בקשות",
    appName: "שם האפליקציה",
    appType: "סוג אינטגרציה",
    apiKey: "מפתח API",
    noApps: "אין אפליקציות עדיין",
    noAppsHint: "צור אינטגרציית API לחיבור שירותים חיצוניים",
    appNamePlaceholder: "האינטגרציה עם ה-CRM שלי",
    deleteConfirm: "למחוק אפליקציה זו?",
    active: "פעיל",
    inactive: "לא פעיל",
    requests: "בקשות",
    copyKey: "העתק מפתח",
    copied: "הועתק!",
  },
  authKey: {
    title: "מפתח אימות",
    yourKey: "מפתח ה-API שלך",
    active: "פעיל",
    keyHint: "השתמש במפתח זה לאימות בקשות API מהאפליקציה שלך.",
    regenerate: "צור מפתח חדש",
    warning: "אזהרה: יצירת מפתח חדש תבטל את הקיים. כל האפליקציות המשתמשות במפתח הנוכחי יפסיקו לעבוד.",
    copied: "הועתק ללוח!",
    usageTitle: "שימוש ב-API",
    usageEndpoint: "כתובת בסיס",
    usageAuth: "אימות",
    usageAuthHint: "כלול את מפתח ה-API בכותרת Authorization:",
  },
};

export type Translations = typeof en;

interface LangContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: Translations;
  isRTL: boolean;
}

const LangContext = createContext<LangContextValue>({
  lang: "en", setLang: () => {}, t: en, isRTL: false,
});

export function LangProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => (localStorage.getItem("wa_lang") as Lang) ?? "en");
  const isRTL = lang === "he";
  const t = (lang === "he" ? he : en) as Translations;

  const setLang = (l: Lang) => { setLangState(l); localStorage.setItem("wa_lang", l); };

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang, isRTL]);

  return <LangContext.Provider value={{ lang, setLang, t, isRTL }}>{children}</LangContext.Provider>;
}

export function useLang() { return useContext(LangContext); }
